import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Users, Clipboard, Trophy, Heart, ArrowLeft } from 'lucide-react';

const roles = [
  {
    id: 'coach',
    title: 'Coach',
    description: 'Create teams, manage rosters, track all game stats.',
    footer: 'Head or assistant coaches.',
    icon: Clipboard,
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-600',
    borderColor: 'border-blue-600',
  },
  {
    id: 'fan',
    title: 'Parent/Fan',
    description: "Track your athlete's games, practice, and progress.",
    footer: 'Best for parents and players under 16.',
    icon: Heart,
    iconBg: 'bg-pink-100',
    iconColor: 'text-pink-600',
    borderColor: 'border-pink-600',
  },
  {
    id: 'player',
    title: 'Player',
    description: 'View your personal stats and performance.',
    footer: 'For athletes ages 16+.',
    icon: Trophy,
    iconBg: 'bg-orange-100',
    iconColor: 'text-orange-600',
    borderColor: 'border-orange-600',
  },
  {
    id: 'staff',
    title: 'Staff',
    description: 'Join teams and assist with stat tracking during games.',
    footer: 'Team staff and stat keepers.',
    icon: Users,
    iconBg: 'bg-emerald-100',
    iconColor: 'text-emerald-600',
    borderColor: 'border-emerald-600',
  },
];

export default function RoleSelection() {
  const [selectedRole, setSelectedRole] = useState(null);
  const [step, setStep] = useState('role'); // 'role' | 'birthdate'
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleContinue = () => {
    if (!selectedRole) return;
    if (selectedRole === 'player') {
      setStep('birthdate');
    } else {
      submitRole();
    }
  };

  const submitRole = async (dob = null) => {
    setLoading(true);
    try {
      const payload = { role: selectedRole };
      if (dob) payload.date_of_birth = dob;
      await api.post('/auth/role', payload);
      toast.success(`Role set to ${selectedRole === 'fan' ? 'Parent/Fan' : selectedRole}`);
      navigate(`/onboarding/${selectedRole}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set role');
    } finally {
      setLoading(false);
    }
  };

  const handleBirthdateSubmit = (e) => {
    e.preventDefault();
    if (!dateOfBirth) {
      toast.error('Please enter your date of birth');
      return;
    }
    submitRole(dateOfBirth);
  };

  if (step === 'birthdate') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white to-slate-50 p-4">
        <div className="max-w-md mx-auto py-12">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-orange-100 mb-4">
              <Trophy className="w-8 h-8 text-orange-600" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight uppercase font-heading text-slate-900 mb-2">
              Player Profile
            </h1>
            <p className="text-base text-slate-500">Enter your date of birth to complete your player profile.</p>
          </div>

          <div className="bg-white border border-slate-200 rounded-lg shadow-md p-8">
            <form onSubmit={handleBirthdateSubmit} className="space-y-6">
              <div>
                <Label htmlFor="dob" className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-2 block">
                  Date of Birth
                </Label>
                <Input
                  id="dob"
                  type="date"
                  data-testid="dob-input"
                  value={dateOfBirth}
                  onChange={(e) => setDateOfBirth(e.target.value)}
                  required
                  max={new Date().toISOString().split('T')[0]}
                  className="h-12 bg-white border-slate-200"
                />
              </div>

              <p className="text-xs text-slate-400 italic leading-relaxed">
                Your date of birth is used to verify eligibility. Players under 16 should register with a parent or guardian account instead.
              </p>

              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setStep('role')}
                  className="h-12 px-4"
                  data-testid="back-to-role-button"
                >
                  <ArrowLeft className="w-4 h-4 mr-1" /> Back
                </Button>
                <Button
                  type="submit"
                  disabled={loading || !dateOfBirth}
                  data-testid="submit-birthdate-button"
                  className="flex-1 h-12 bg-slate-900 hover:bg-slate-800 text-white font-bold uppercase tracking-wide shadow-md hover:shadow-lg transition-all active:scale-95 disabled:opacity-50"
                >
                  {loading ? 'Setting up...' : 'Continue'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-slate-50 p-4">
      <div className="max-w-3xl mx-auto py-12">
        <div className="text-center mb-10">
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight uppercase font-heading text-slate-900 mb-3">
            Choose Your Role
          </h1>
          <p className="text-lg text-slate-500">Select how you'll use Smarter Hoops.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-10">
          {roles.map((role) => {
            const Icon = role.icon;
            const isSelected = selectedRole === role.id;
            return (
              <button
                key={role.id}
                data-testid={`role-${role.id}-button`}
                onClick={() => setSelectedRole(role.id)}
                className={`
                  bg-white border-2 rounded-xl p-6 text-left transition-all
                  hover:shadow-md active:scale-[0.98]
                  ${isSelected ? `${role.borderColor} shadow-md` : 'border-slate-200 shadow-sm'}
                `}
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className={`${role.iconBg} p-3 rounded-xl shrink-0`}>
                    <Icon className={`w-7 h-7 ${role.iconColor}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-xl font-bold font-heading tracking-tight text-slate-900 mb-1">
                      {role.title}
                    </h3>
                    <p className="text-sm text-slate-600 leading-relaxed">{role.description}</p>
                  </div>
                </div>
                <p className="text-xs text-slate-400 pl-[60px]">{role.footer}</p>
              </button>
            );
          })}
        </div>

        <div className="text-center">
          <Button
            onClick={handleContinue}
            disabled={!selectedRole || loading}
            data-testid="continue-button"
            className="h-12 px-10 bg-slate-900 hover:bg-slate-800 text-white font-bold uppercase tracking-wide shadow-md hover:shadow-lg transition-all active:scale-95 disabled:opacity-50"
          >
            {loading ? 'Setting up...' : 'Continue'}
          </Button>
        </div>
      </div>
    </div>
  );
}
